import { NotificationGroup } from "./NotificationPreferences.Interface";

// ---------------------------------------------------------------------------
// Event enum – one entry per dispatchable notification
// ---------------------------------------------------------------------------

export enum NotificationEvent {
  TICKET_ASSIGNEE_CHANGED = "ticket_assignee_changed",
  TICKET_NOTE_ADDED = "ticket_note_added",
  TICKET_SUBSCRIBED_NOTE_ADDED = "ticket_subscribed_note_added",
  TICKET_OPEN_TO_GROUP = "ticket_open_to_group",
  TICKET_SUBSCRIBED_ASSIGNEE_CHANGED = "ticket_subscribed_assignee_changed",
  TICKET_SUBSCRIBED_OPEN_TO_GROUP = "ticket_subscribed_open_to_group",
  ESCALATION_AGENT_ASSIGNED = "escalation_agent_assigned",
  ESCALATION_AGENT_ASSIGNED_V3 = "escalation_agent_assigned_v3"
}

// ---------------------------------------------------------------------------
// Typed variables for each event
// ---------------------------------------------------------------------------

export interface NotificationEventVars {
  [NotificationEvent.TICKET_ASSIGNEE_CHANGED]: {
    ticketNumber: string;
    ticketTitle: string;
    ticketId: number;
    assignerName: string;
    ticketDescription: string;
    ticketDueDate: string;
    ticketPriority: string;
    ticketStatus: string;
    ticketUrl: string;
    customerName: string;
  };
  [NotificationEvent.TICKET_NOTE_ADDED]: {
    ticketNumber: string;
    ticketTitle: string;
    ticketId: number;
    assignerName: string;
    ticketDescription: string;
    ticketDueDate: string;
    ticketPriority: string;
    ticketStatus: string;
    ticketUrl: string;
    customerName: string;
    noteBody: string;
    noteBodyPreview: string;
  };
  [NotificationEvent.TICKET_SUBSCRIBED_NOTE_ADDED]: {
    ticketNumber: string;
    ticketTitle: string;
    ticketId: number;
    assignerName: string;
    ticketDescription: string;
    ticketDueDate: string;
    ticketPriority: string;
    ticketStatus: string;
    ticketUrl: string;
    customerName: string;
    noteBody: string;
    noteBodyPreview: string;
  };
  [NotificationEvent.TICKET_OPEN_TO_GROUP]: {
    ticketNumber: string;
    ticketTitle: string;
    ticketId: number;
    actorDisplayName: string;
    groupName: string;
    customerName: string;
    ticketDescription: string;
    ticketDueDate: string;
    ticketPriority: string;
    ticketStatus: string;
    ticketUrl: string;
  };
  [NotificationEvent.TICKET_SUBSCRIBED_ASSIGNEE_CHANGED]: {
    ticketNumber: string;
    ticketTitle: string;
    ticketId: number;
    assignerName: string;
    assigneeName: string;
    ticketDescription: string;
    ticketDueDate: string;
    ticketPriority: string;
    ticketStatus: string;
    ticketUrl: string;
    customerName: string;
  };
  [NotificationEvent.TICKET_SUBSCRIBED_OPEN_TO_GROUP]: {
    ticketNumber: string;
    ticketTitle: string;
    ticketId: number;
    actorDisplayName: string;
    groupName: string;
    customerName: string;
    ticketDescription: string;
    ticketDueDate: string;
    ticketPriority: string;
    ticketStatus: string;
    ticketUrl: string;
  };
  [NotificationEvent.ESCALATION_AGENT_ASSIGNED]: {
    escalationId: number;
    userId: number;
    channel: string;
  };
  [NotificationEvent.ESCALATION_AGENT_ASSIGNED_V3]: {
    escalationId: number;
    userId: number;
    /** Deep-links the v3 page to the escalated channel's conversation, not the user's first (ENG-3475). */
    botConversationId: number;
  };
}

// ---------------------------------------------------------------------------
// Channel message template interfaces
// ---------------------------------------------------------------------------

export interface ChannelMessageTemplate {
  title: string;
  body: string;
  url?: string;
}

export interface EmailMessageTemplate extends ChannelMessageTemplate {
  /** Mandrill template slug. When set, email is sent via sendTemplate. */
  mandrillTemplateName?: string;
  /** Maps Mandrill merge-var names → interpolation templates. */
  mergeVarMap?: Record<string, string>;
  /** Mint a ticketAccessUrl merge var once the recipient's email is known. */
  needsTicketAccessUrl?: boolean;
}

export interface PushMessageTemplate extends ChannelMessageTemplate {
  /** Push notification tag – used for grouping/replacing notifications on the device. */
  tag?: string;
}

export interface ToastMessageTemplate {
  title: string;
  subtitle: string;
  actionLabel?: string;
  actionPath?: string;
}

export interface NotificationEventConfig<E extends NotificationEvent = NotificationEvent> {
  event: E;
  group: NotificationGroup;
  desktop: ChannelMessageTemplate;
  push: PushMessageTemplate;
  sms: ChannelMessageTemplate;
  email: EmailMessageTemplate;
  slack: ChannelMessageTemplate;
  /** Optional frontend toast template (for local WebSocket-driven toasts). */
  toast?: ToastMessageTemplate;
  /** When true, skip the in-app desktop toast (duplicate toasts were being shown). Use when a dedicated
   *  socket event already renders the appropriate UI. */
  skipInAppToast?: boolean;
}

// ---------------------------------------------------------------------------
// The config – single source of truth for all notification messages
// ---------------------------------------------------------------------------

export const NOTIFICATION_MESSAGES: { [E in NotificationEvent]: NotificationEventConfig<E> } = {
  [NotificationEvent.TICKET_ASSIGNEE_CHANGED]: {
    event: NotificationEvent.TICKET_ASSIGNEE_CHANGED,
    group: NotificationGroup.TICKET_ASSIGNED,
    desktop: {
      title: "Ticket #${ticketNumber} assigned to you",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    push: {
      title: "Ticket #${ticketNumber} assigned to you",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}",
      tag: "notification"
    },
    sms: {
      title: "Ticket #${ticketNumber} assigned to you",
      body: "\nTitle - ${ticketTitle}\nCustomer - ${customerName}\nAssigned by - ${assignerName}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    email: {
      title: "Ticket #${ticketNumber} assigned to you",
      body: "${ticketTitle}",
      mandrillTemplateName: "notification-ticketing-assignment",
      needsTicketAccessUrl: true,
      mergeVarMap: {
        TICKET_NUMBER: "${ticketNumber}",
        TICKET_TITLE: "${ticketTitle}",
        TICKET_ASSIGNER_NAME: "${assignerName}",
        TICKET_DESCRIPTION: "${ticketDescription}",
        TICKET_DUE_DATE: "${ticketDueDate}",
        TICKET_PRIORITY: "${ticketPriority}",
        TICKET_RECIPIENT_EMAIL: "${recipientEmail}",
        TICKET_STATUS: "${ticketStatus}",
        TICKET_URL: "${ticketUrl}",
        TICKET_CLOSE_URL: "${ticketAccessUrl}"
      }
    },
    slack: {
      title: "Ticket #${ticketNumber} assigned to you",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    toast: {
      title: "Ticket #${ticketNumber} assigned to you",
      subtitle: "${ticketTitle}",
      actionLabel: "View",
      actionPath: "/inbox/ticket/${ticketNumber}"
    }
  },

  [NotificationEvent.TICKET_NOTE_ADDED]: {
    event: NotificationEvent.TICKET_NOTE_ADDED,
    group: NotificationGroup.TICKET_NOTES,
    desktop: {
      title: "New comment on ticket #${ticketNumber}",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    push: {
      title: "New comment on ticket #${ticketNumber}",
      body: '${assignerName} said "${noteBodyPreview}"',
      url: "/inbox/ticket/${ticketNumber}",
      tag: "notification"
    },
    sms: {
      title: "New comment on ticket #${ticketNumber}",
      body: '\n${assignerName} said "${noteBodyPreview}"\nTitle - ${ticketTitle}\nCustomer - ${customerName}',
      url: "/inbox/ticket/${ticketNumber}"
    },
    email: {
      title: "New comment on ticket #${ticketNumber}",
      body: "${ticketTitle}",
      mandrillTemplateName: "notification-ticketing-comment",
      needsTicketAccessUrl: true,
      mergeVarMap: {
        TICKET_NUMBER: "${ticketNumber}",
        TICKET_TITLE: "${ticketTitle}",
        TICKET_ASSIGNER_NAME: "${assignerName}",
        TICKET_COMMENT: "${noteBody}",
        TICKET_DESCRIPTION: "${ticketDescription}",
        TICKET_DUE_DATE: "${ticketDueDate}",
        TICKET_PRIORITY: "${ticketPriority}",
        TICKET_RECIPIENT_EMAIL: "${recipientEmail}",
        TICKET_STATUS: "${ticketStatus}",
        TICKET_URL: "${ticketUrl}",
        TICKET_CLOSE_URL: "${ticketAccessUrl}"
      }
    },
    slack: {
      title: "New comment on ticket #${ticketNumber}",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    toast: {
      title: "${ticketTitle}",
      subtitle: "New comment on ticket #${ticketNumber}",
      actionLabel: "View",
      actionPath: "/inbox/ticket/${ticketNumber}"
    }
  },

  [NotificationEvent.TICKET_SUBSCRIBED_NOTE_ADDED]: {
    event: NotificationEvent.TICKET_SUBSCRIBED_NOTE_ADDED,
    group: NotificationGroup.TICKET_SUBSCRIBED,
    desktop: {
      title: "New comment on ticket #${ticketNumber}",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    push: {
      title: "New comment on ticket #${ticketNumber}",
      body: '${assignerName} said "${noteBodyPreview}"',
      url: "/inbox/ticket/${ticketNumber}",
      tag: "notification"
    },
    sms: {
      title: "New comment on ticket #${ticketNumber}",
      body: '\n${assignerName} said "${noteBodyPreview}"\nTitle - ${ticketTitle}\nCustomer - ${customerName}',
      url: "/inbox/ticket/${ticketNumber}"
    },
    email: {
      title: "New comment on ticket #${ticketNumber}",
      body: "${ticketTitle}",
      mandrillTemplateName: "notification-ticketing-comment",
      needsTicketAccessUrl: true,
      mergeVarMap: {
        TICKET_NUMBER: "${ticketNumber}",
        TICKET_TITLE: "${ticketTitle}",
        TICKET_ASSIGNER_NAME: "${assignerName}",
        TICKET_COMMENT: "${noteBody}",
        TICKET_DESCRIPTION: "${ticketDescription}",
        TICKET_DUE_DATE: "${ticketDueDate}",
        TICKET_PRIORITY: "${ticketPriority}",
        TICKET_RECIPIENT_EMAIL: "${recipientEmail}",
        TICKET_STATUS: "${ticketStatus}",
        TICKET_URL: "${ticketUrl}",
        TICKET_CLOSE_URL: "${ticketAccessUrl}"
      }
    },
    slack: {
      title: "New comment on ticket #${ticketNumber}",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    toast: {
      title: "${ticketTitle}",
      subtitle: "New comment on ticket #${ticketNumber}",
      actionLabel: "View",
      actionPath: "/inbox/ticket/${ticketNumber}"
    }
  },

  [NotificationEvent.TICKET_OPEN_TO_GROUP]: {
    event: NotificationEvent.TICKET_OPEN_TO_GROUP,
    group: NotificationGroup.TICKET_GROUP,
    desktop: {
      title: "Ticket #${ticketNumber} assigned to the ${groupName} group",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    push: {
      title: "Ticket #${ticketNumber} assigned to the ${groupName} group",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}",
      tag: "notification"
    },
    sms: {
      title: "Ticket #${ticketNumber} assigned to the ${groupName} group",
      body: "\nTitle - ${ticketTitle}\nCustomer - ${customerName}\nAssigned by - ${actorDisplayName}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    email: {
      title: "Ticket #${ticketNumber} assigned to the ${groupName} group",
      body: "${ticketTitle}",
      mandrillTemplateName: "notification-ticketing-group-dispatch",
      mergeVarMap: {
        TICKET_NUMBER: "${ticketNumber}",
        TICKET_TITLE: "${ticketTitle}",
        TICKET_ASSIGNER_NAME: "${actorDisplayName}",
        TICKET_DESCRIPTION: "${ticketDescription}",
        TICKET_DUE_DATE: "${ticketDueDate}",
        TICKET_GROUP_NAME: "${groupName}",
        TICKET_PRIORITY: "${ticketPriority}",
        TICKET_RECIPIENT_EMAIL: "${recipientEmail}",
        TICKET_STATUS: "${ticketStatus}",
        TICKET_URL: "${ticketUrl}"
      }
    },
    slack: {
      title: "Ticket #${ticketNumber} assigned to the ${groupName} group",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    toast: {
      title: "Ticket #${ticketNumber} assigned to the ${groupName} group",
      subtitle: "${ticketTitle}",
      actionLabel: "View",
      actionPath: "/inbox/ticket/${ticketNumber}"
    }
  },

  // Subscriber variants of assignee/group events; copy must stay neutral.
  [NotificationEvent.TICKET_SUBSCRIBED_ASSIGNEE_CHANGED]: {
    event: NotificationEvent.TICKET_SUBSCRIBED_ASSIGNEE_CHANGED,
    group: NotificationGroup.TICKET_SUBSCRIBED,
    desktop: {
      title: "Ticket #${ticketNumber} assigned to ${assigneeName}",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    push: {
      title: "Ticket #${ticketNumber} assigned to ${assigneeName}",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}",
      tag: "notification"
    },
    sms: {
      title: "Ticket #${ticketNumber} assigned to ${assigneeName}",
      body: "\nTitle - ${ticketTitle}\nCustomer - ${customerName}\nAssigned by - ${assignerName}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    email: {
      title: "Ticket #${ticketNumber} assigned to ${assigneeName}",
      body: "${ticketTitle}",
      mandrillTemplateName: "notification-ticketing-subscribed-assignment",
      needsTicketAccessUrl: true,
      mergeVarMap: {
        TICKET_NUMBER: "${ticketNumber}",
        TICKET_TITLE: "${ticketTitle}",
        TICKET_ASSIGNER_NAME: "${assignerName}",
        TICKET_ASSIGNEE_NAME: "${assigneeName}",
        TICKET_DESCRIPTION: "${ticketDescription}",
        TICKET_DUE_DATE: "${ticketDueDate}",
        TICKET_PRIORITY: "${ticketPriority}",
        TICKET_RECIPIENT_EMAIL: "${recipientEmail}",
        TICKET_STATUS: "${ticketStatus}",
        TICKET_URL: "${ticketUrl}",
        TICKET_CLOSE_URL: "${ticketAccessUrl}"
      }
    },
    slack: {
      title: "Ticket #${ticketNumber} assigned to ${assigneeName}",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    toast: {
      title: "Ticket #${ticketNumber} assigned to ${assigneeName}",
      subtitle: "${ticketTitle}",
      actionLabel: "View",
      actionPath: "/inbox/ticket/${ticketNumber}"
    }
  },

  [NotificationEvent.TICKET_SUBSCRIBED_OPEN_TO_GROUP]: {
    event: NotificationEvent.TICKET_SUBSCRIBED_OPEN_TO_GROUP,
    group: NotificationGroup.TICKET_SUBSCRIBED,
    desktop: {
      title: "Ticket #${ticketNumber} assigned to the ${groupName} group",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    push: {
      title: "Ticket #${ticketNumber} assigned to the ${groupName} group",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}",
      tag: "notification"
    },
    sms: {
      title: "Ticket #${ticketNumber} assigned to the ${groupName} group",
      body: "\nTitle - ${ticketTitle}\nCustomer - ${customerName}\nAssigned by - ${actorDisplayName}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    email: {
      title: "Ticket #${ticketNumber} assigned to the ${groupName} group",
      body: "${ticketTitle}",
      // Reused: copy is already recipient-neutral, unlike assignee-changed.
      mandrillTemplateName: "notification-ticketing-group-dispatch",
      mergeVarMap: {
        TICKET_NUMBER: "${ticketNumber}",
        TICKET_TITLE: "${ticketTitle}",
        TICKET_ASSIGNER_NAME: "${actorDisplayName}",
        TICKET_DESCRIPTION: "${ticketDescription}",
        TICKET_DUE_DATE: "${ticketDueDate}",
        TICKET_GROUP_NAME: "${groupName}",
        TICKET_PRIORITY: "${ticketPriority}",
        TICKET_RECIPIENT_EMAIL: "${recipientEmail}",
        TICKET_STATUS: "${ticketStatus}",
        TICKET_URL: "${ticketUrl}"
      }
    },
    slack: {
      title: "Ticket #${ticketNumber} assigned to the ${groupName} group",
      body: "${ticketTitle}",
      url: "/inbox/ticket/${ticketNumber}"
    },
    toast: {
      title: "Ticket #${ticketNumber} assigned to the ${groupName} group",
      subtitle: "${ticketTitle}",
      actionLabel: "View",
      actionPath: "/inbox/ticket/${ticketNumber}"
    }
  },

  [NotificationEvent.ESCALATION_AGENT_ASSIGNED]: {
    event: NotificationEvent.ESCALATION_AGENT_ASSIGNED,
    group: NotificationGroup.CONVERSATIONS,
    skipInAppToast: true,
    desktop: {
      title: "New escalation assigned",
      body: "A customer is waiting for you",
      url: "/conversations/user/${userId}?channel=${channel}"
    },
    push: {
      title: "New escalation assigned",
      body: "A customer is waiting for you",
      url: "/conversations/user/${userId}?channel=${channel}",
      tag: "escalation:${escalationId}"
    },
    sms: {
      title: "New escalation assigned",
      body: "A customer is waiting for you",
      url: "/conversations/user/${userId}?channel=${channel}"
    },
    email: {
      title: "New escalation assigned",
      body: "A customer is waiting for you",
      mandrillTemplateName: "swivl-escalation-assigned",
      mergeVarMap: {
        USER_URL: "/conversations/user/${userId}?channel=${channel}"
      }
    },
    slack: {
      title: "New escalation assigned",
      body: "A customer is waiting for you",
      url: "/conversations/user/${userId}?channel=${channel}"
    }
  },

  // CC v3 (Snowy) escalations link to the v3 conversation route. The legacy
  // event above stays on the legacy Contact Center path (ENG-3215).
  [NotificationEvent.ESCALATION_AGENT_ASSIGNED_V3]: {
    event: NotificationEvent.ESCALATION_AGENT_ASSIGNED_V3,
    group: NotificationGroup.CONVERSATIONS,
    skipInAppToast: true,
    desktop: {
      title: "New escalation assigned",
      body: "A customer is waiting for you",
      url: "/contact-center-v3/conversations/${userId}?conversation=${botConversationId}"
    },
    push: {
      title: "New escalation assigned",
      body: "A customer is waiting for you",
      url: "/contact-center-v3/conversations/${userId}?conversation=${botConversationId}",
      tag: "escalation:${escalationId}"
    },
    sms: {
      title: "New escalation assigned",
      body: "A customer is waiting for you",
      url: "/contact-center-v3/conversations/${userId}?conversation=${botConversationId}"
    },
    email: {
      title: "New escalation assigned",
      body: "A customer is waiting for you",
      mandrillTemplateName: "swivl-escalation-assigned",
      mergeVarMap: {
        USER_URL: "/contact-center-v3/conversations/${userId}?conversation=${botConversationId}"
      }
    },
    slack: {
      title: "New escalation assigned",
      body: "A customer is waiting for you",
      url: "/contact-center-v3/conversations/${userId}?conversation=${botConversationId}"
    }
  }
};

// ---------------------------------------------------------------------------
// Interpolation utility – shared by server and frontend
// ---------------------------------------------------------------------------

export function interpolateTemplate(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\$\{(\w+)\}/g, (_, key) => {
    return key in vars ? String(vars[key]) : `\${${key}}`;
  });
}

// Coerces every value to a string, for template interpolation.
export function stringifyVars(vars: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries(Object.entries(vars).map(([k, v]) => [k, String(v)]));
}
